import * as React from 'react';
import ReactTooltip from 'react-tooltip';
import Xarrow, { anchorType } from 'react-xarrows';
import styled, { css } from 'styled-components';
import {
  getActiveDeviceMaskClass,
  getDeviceIndex,
  getOutputDevices,
  isDeviceActive,
  getInputDevices,
  onDeviceItemClick,
  connectionArrowsFilterFunction,
  hasInputDeviceConnections,
  isDeviceDisabled,
  isDeviceGrayed,
  composeSceneConnectionId,
  isSceneActive,
  onSceneItemClick,
  sceneDisabledBasedOnSceneConnection,
  AnchorFromOutput,
  isInputButtonDisabled,
  deviceDisabledBasedOnSceneConnection,
  AnchorFromInput
} from './connections.logic';
import {
  getBuzzerSelectValue,
  getFrameLedSelectedColors,
  onBuzzerMelodySelect,
  shouldPressProfileSelectionForRoomBeVisible,
  onFrameLedColorSelect,
  shouldPressProfileSelectionForScenesBeVisible
} from './AdvancedConnections.logic';
import {
  getDeviceIconBySymbol,
  devices as deviceTypes,
  buzzerMelodies,
  buzzerMelodiesSelectOptions,
  PressProfiles,
  ledBrightnessPercentConvert,
  getSnapInIcon
} from '../../util/devices';
import { getAssetPath } from '../../util/images';
import { SvgImage } from '../SvgImage';
import ConnectionDeviceButton from '../ConnectionDeviceButton/ConnectionDeviceButton';
import ColorSelectDropdown, {
  noColorCircleMixin
} from '../ColorSelectDropdown/ColorSelectDropdown';
import './connections.scss';
import {
  BuzzerPlayButton,
  ConfigurationArrowsContainer,
  ConnectionDeviceButtonWrapper,
  DockRenderWrapper,
  FixedHeaderColumn,
  FlexContainer,
  MainContainer,
  OutputDeviceProfileButtonContainer,
  OutputDeviceProfileContainer,
  PressProfileButton,
  RoomToggle,
  Row,
  SceneTitleWrapper,
  ScrollableContainer,
  ScrollableWrapper,
  SnapInDescription,
  SnapInImageContainer,
  SoundSelector,
  SoundSelectorCointainer
} from './ConnectionsComponents';
import {
  Dock,
  DocksMap,
  IInputDevice,
  Room,
  Scene,
  SCENE_ACTION
} from './types';
import { SceneIcon } from './Svg';
import { Text } from '../Text/Text';

const PressProfilesContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
`;
const PreviewText = styled.span`
  color: ${({ theme }) => theme.colors.font1};
`;
const ColorPreviewContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
`;
const PreviewColorCircle = styled.div<{ color: any }>`
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid #e6e6e6;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
  ${({ color }) => {
    if (!color) {
      return noColorCircleMixin();
    }
    return css`
      background-color: ${() => `hsl(${color.hue}, ${color.saturation}%, 50%)`};
    `;
  }}
`;

export interface IAdvancedConnectionsPanelProps {
  rooms: Room[];
  docks: DocksMap;
  scenes: Scene[];
  updateConnectionsHandler: (localConnections: any) => void;
  deviceConnections: any;
  previewMode: boolean;
}
const AdvancedConnectionsPanel: React.FC<IAdvancedConnectionsPanelProps> = ({
  rooms,
  docks,
  scenes,
  updateConnectionsHandler,
  deviceConnections,
  previewMode
}) => {
  const [refresh, setRefresh] = React.useState(0);
  const [inputDevice, setInputDevice] = React.useState<IInputDevice | null>(
    null
  );
  const [roomsVisibilityStatus, setRoomsVisibilityStatus] = React.useState<{}>(
    {}
  );
  const [
    connectionsConfiguration,
    setConnectionsConfiguration
  ] = React.useState([]);
  const [pressProfile, setPressProfile] = React.useState<PressProfiles>(
    PressProfiles.singlePress
  );

  const onRoomRollup = (roomDocks: Dock[]) => {
    const inputInCurrentRoom = roomDocks.find(
      el =>
        inputDevice &&
        el.number === inputDevice.connectionInputConfig.sourceDockNumber
    );
    return inputInCurrentRoom && setInputDevice(null);
  };

  const arrowsRefreshHandler = () => {
    setRefresh(prev => prev + 1);
  };

  const updateConnections = connections => {
    setConnectionsConfiguration(connections);
    updateConnectionsHandler(
      connections.map(devicesConnection => devicesConnection.connection)
    );
  };

  React.useEffect(() => {
    if (inputDevice) {
      const { connectionInputConfig } = inputDevice;
      setInputDevice({
        ...inputDevice,
        connectionInputConfig: {
          ...connectionInputConfig,
          triggerEvent: pressProfile
        }
      });
    }
  }, [pressProfile]);

  React.useEffect(() => {
    updateConnections(deviceConnections);
    ReactTooltip.rebuild();
  }, [deviceConnections]);

  // If Preview mode is toggling, input device need to be cleared
  React.useEffect(() => setInputDevice(null), [previewMode]);

  return (
    <MainContainer>
      <ScrollableContainer>
        <Row>
          <FixedHeaderColumn>Snap-ins list</FixedHeaderColumn>
          <FlexContainer>
            <ScrollableWrapper
              flexBasis="200"
              display="flex"
              justifyContent="center"
            >
              1. Choose input
            </ScrollableWrapper>
            <ScrollableWrapper
              flexBasis="232"
              display="flex"
              justifyContent="center"
            >
              2. Select profile
            </ScrollableWrapper>
            <ScrollableWrapper
              flexBasis="256"
              display="flex"
              justifyContent="center"
            >
              3. Connect with output
            </ScrollableWrapper>
            <ScrollableWrapper
              flexBasis="264"
              display="flex"
              justifyContent="center"
            >
              4. Select profile
            </ScrollableWrapper>
          </FlexContainer>
        </Row>

        {rooms.map(room => (
          <Row key={`room_${room.id}`}>
            <RoomToggle
              opened={!roomsVisibilityStatus[room.id]}
              roomName={room.name}
              onClick={() => {
                const currentVisibilityStatus = roomsVisibilityStatus[room.id];
                setRoomsVisibilityStatus({
                  ...roomsVisibilityStatus,
                  [room.id]: !currentVisibilityStatus
                });
                onRoomRollup(docks[room.id]);

                setImmediate(() => {
                  ReactTooltip.rebuild();
                  arrowsRefreshHandler();
                });
              }}
            />
            {!roomsVisibilityStatus[room.id] &&
              docks[room.id].map(dock => {
                const SnapInImage = getSnapInIcon(dock.snapInCode);

                return (
                  <FlexContainer key={`dock_${dock.number}`}>
                    <DockRenderWrapper>
                      <FixedHeaderColumn>
                        <SnapInImageContainer>
                          {getActiveDeviceMaskClass(
                            connectionsConfiguration,
                            inputDevice,
                            dock,
                            pressProfile
                          ).map(classes => (
                            <div
                              className={`device-mask ${classes}`}
                              key={classes}
                            />
                          ))}
                          <SnapInImage />
                          <ReactTooltip />
                          <div
                            data-tip={`Zigbee ID: ${dock.zigbeeId ||
                              'unknown'}`}
                          >
                            <SvgImage
                              inAssetsPath={getAssetPath(
                                'icons/devices-location-configuration.svg'
                              )}
                              className="devices-configuration-snap-in-info-icon"
                            />
                          </div>
                        </SnapInImageContainer>
                        <SnapInDescription
                          className="ellipsis"
                          data-tip={`Dock: ${dock.number}`}
                        >
                          Dock: {dock.number}
                        </SnapInDescription>
                        <p className="devices-configuration-snap-in-label">
                          {dock.positionId}
                        </p>
                      </FixedHeaderColumn>
                    </DockRenderWrapper>

                    <FlexContainer>
                      <ScrollableWrapper>
                        {getInputDevices(dock.devices).map(device => {
                          const isActive = isDeviceActive(
                            connectionsConfiguration,
                            inputDevice,
                            device,
                            dock,
                            'input',
                            pressProfile
                          );

                          return (
                            <ConnectionDeviceButtonWrapper
                              key={`device_${device.id}`}
                            >
                              <ConnectionDeviceButton
                                label={device.name}
                                icon={getDeviceIconBySymbol(device.symbol)}
                                deviceLinked={
                                  !isActive &&
                                  hasInputDeviceConnections(
                                    device,
                                    dock,
                                    connectionsConfiguration
                                  )
                                }
                                disabled={
                                  !previewMode &&
                                  (isDeviceDisabled(
                                    device,
                                    inputDevice,
                                    'output'
                                  ) ||
                                    isInputButtonDisabled(
                                      device,
                                      dock,
                                      inputDevice,
                                      connectionsConfiguration,
                                      getDeviceIndex(dock.devices, device),
                                      pressProfile
                                    ))
                                }
                                grayed={isDeviceGrayed(
                                  previewMode,
                                  isActive,
                                  inputDevice,
                                  dock,
                                  device,
                                  'input'
                                )}
                                activeCircle={{
                                  right:
                                    isActive &&
                                    inputDevice &&
                                    inputDevice.startsFrom === 'input'
                                }}
                                active={isActive}
                                id={`input_${dock.number}_${getDeviceIndex(
                                  dock.devices,
                                  device
                                )}`}
                                onClick={() =>
                                  onDeviceItemClick(
                                    dock,
                                    device,
                                    'input',
                                    inputDevice,
                                    setInputDevice,
                                    pressProfile,
                                    connectionsConfiguration,
                                    updateConnections,
                                    previewMode
                                  )
                                }
                              />
                            </ConnectionDeviceButtonWrapper>
                          );
                        })}
                      </ScrollableWrapper>
                      <ScrollableWrapper
                        flexBasis="200"
                        display="flex"
                        alignItems="center"
                      >
                        {shouldPressProfileSelectionForRoomBeVisible(
                          dock.number,
                          inputDevice
                        ) && (
                          <PressProfilesContainer>
                            <PressProfileButton
                              active={
                                pressProfile === PressProfiles.singlePress
                              }
                              type="button"
                              id={`${PressProfiles.singlePress}${inputDevice.id}ProfileBtn`}
                              onClick={() =>
                                setPressProfile(PressProfiles.singlePress)
                              }
                            >
                              Single press
                            </PressProfileButton>
                            <PressProfileButton
                              active={
                                pressProfile === PressProfiles.doublePress
                              }
                              type="button"
                              id={`${PressProfiles.doublePress}${inputDevice.id}ProfileBtn`}
                              onClick={() =>
                                setPressProfile(PressProfiles.doublePress)
                              }
                            >
                              Double press
                            </PressProfileButton>
                          </PressProfilesContainer>
                        )}
                      </ScrollableWrapper>
                      <ScrollableWrapper>
                        {getOutputDevices(dock.devices).map(device => {
                          const isActive = isDeviceActive(
                            connectionsConfiguration,
                            inputDevice,
                            device,
                            dock,
                            'output',
                            pressProfile
                          );

                          return (
                            <OutputDeviceProfileButtonContainer
                              key={`device_${device.id}`}
                            >
                              <ConnectionDeviceButton
                                key={`device_${device.id}`}
                                label={device.name}
                                icon={getDeviceIconBySymbol(device.symbol)}
                                active={isActive}
                                activeCircle={{
                                  left:
                                    isActive &&
                                    inputDevice &&
                                    inputDevice.startsFrom === 'output'
                                }}
                                disabled={
                                  !previewMode &&
                                  (isDeviceDisabled(
                                    device,
                                    inputDevice,
                                    'input'
                                  ) ||
                                    deviceDisabledBasedOnSceneConnection(
                                      connectionsConfiguration,
                                      inputDevice
                                    ))
                                }
                                grayed={isDeviceGrayed(
                                  previewMode,
                                  isActive,
                                  inputDevice,
                                  dock,
                                  device,
                                  'output'
                                )}
                                id={`output_${dock.number}_${getDeviceIndex(
                                  dock.devices,
                                  device
                                )}`}
                                onClick={() =>
                                  onDeviceItemClick(
                                    dock,
                                    device,
                                    'output',
                                    inputDevice,
                                    setInputDevice,
                                    pressProfile,
                                    connectionsConfiguration,
                                    updateConnections,
                                    previewMode
                                  )
                                }
                              />
                              <OutputDeviceProfileContainer>
                                {isActive &&
                                  device.symbol === deviceTypes.buzzer.symbol &&
                                  (() => {
                                    const deviceIndex = getDeviceIndex(
                                      dock.devices,
                                      device
                                    );
                                    const buzzerSoundSelectValue = getBuzzerSelectValue(
                                      inputDevice,
                                      connectionsConfiguration,
                                      pressProfile,
                                      deviceIndex,
                                      dock
                                    );
                                    const buzzerSoundAsset =
                                      buzzerMelodies[
                                        buzzerSoundSelectValue.value
                                      ];

                                    return (
                                      <div
                                        id={`BuzzerMelodySelector${device.id}`}
                                      >
                                        <SoundSelectorCointainer>
                                          {previewMode ? (
                                            <PreviewText>
                                              {buzzerSoundSelectValue.label}
                                            </PreviewText>
                                          ) : (
                                            <SoundSelector
                                              value={buzzerSoundSelectValue}
                                              onChange={melody => {
                                                onBuzzerMelodySelect(
                                                  inputDevice,
                                                  connectionsConfiguration,
                                                  pressProfile,
                                                  setInputDevice,
                                                  deviceIndex,
                                                  dock,
                                                  updateConnections,
                                                  melody
                                                );
                                              }}
                                              placeholder="Select buzzer melody"
                                              options={
                                                buzzerMelodiesSelectOptions
                                              }
                                            />
                                          )}
                                          {buzzerSoundAsset && (
                                            // eslint-disable-next-line jsx-a11y/media-has-caption
                                            <audio
                                              id={`buzzerMelodyPlayer${device.id}`}
                                              src={getAssetPath(
                                                buzzerSoundAsset.url
                                              )}
                                            />
                                          )}
                                          {buzzerSoundAsset && (
                                            <BuzzerPlayButton
                                              icon={`${getAssetPath(
                                                'icons/play.svg'
                                              )}`}
                                              type="button"
                                              onClick={() => {
                                                document
                                                  .getElementById(
                                                    `buzzerMelodyPlayer${device.id}`
                                                  )
                                                  // @ts-ignore
                                                  .play();
                                              }}
                                            />
                                          )}
                                        </SoundSelectorCointainer>
                                        <SoundSelectorCointainer>
                                          <ConfigurationArrowsContainer>
                                            <Xarrow
                                              path="grid"
                                              startAnchor="right"
                                              endAnchor="left"
                                              start={`output_${dock.number}_${deviceIndex}`}
                                              end={`BuzzerMelodySelector${device.id}`}
                                              key={`buzzerMelodyArrow${device.id}`}
                                              strokeWidth={2}
                                              headSize={4}
                                            />
                                          </ConfigurationArrowsContainer>
                                        </SoundSelectorCointainer>
                                      </div>
                                    );
                                  })()}

                                {isActive &&
                                  device.symbol ===
                                    deviceTypes.frameLed.symbol &&
                                  (() => {
                                    const deviceIndex = getDeviceIndex(
                                      dock.devices,
                                      device
                                    );
                                    const frameLedSelectedColors = getFrameLedSelectedColors(
                                      inputDevice,
                                      connectionsConfiguration,
                                      pressProfile,
                                      deviceIndex,
                                      dock
                                    );
                                    const {
                                      connectionInputConfig
                                    } = inputDevice;
                                    const {
                                      targetActionParameters = {}
                                    } = connectionInputConfig;
                                    const {
                                      Color,
                                      Brightness
                                    } = targetActionParameters;
                                    const selectedFrameLedColor = frameLedSelectedColors.length
                                      ? frameLedSelectedColors[0]
                                      : {
                                          Color,
                                          Brightness
                                        };

                                    return (
                                      <div
                                        id={`FrameLedColorSelector${device.id}`}
                                      >
                                        <SoundSelectorCointainer>
                                          {previewMode ? (
                                            <ColorPreviewContainer>
                                              <PreviewColorCircle
                                                color={
                                                  selectedFrameLedColor.Color
                                                }
                                              />
                                              <PreviewText>
                                                {frameLedSelectedColors.length ===
                                                1
                                                  ? `${ledBrightnessPercentConvert(
                                                      selectedFrameLedColor.Brightness
                                                    ) || 0}%`
                                                  : 'More than 1 color'}
                                              </PreviewText>
                                            </ColorPreviewContainer>
                                          ) : (
                                            <ColorSelectDropdown
                                              defaultColor={
                                                selectedFrameLedColor
                                              }
                                              frameLedSelectedColors={
                                                frameLedSelectedColors
                                              }
                                              onFrameLedColorSelect={selectedColor =>
                                                onFrameLedColorSelect(
                                                  inputDevice,
                                                  connectionsConfiguration,
                                                  pressProfile,
                                                  setInputDevice,
                                                  deviceIndex,
                                                  dock,
                                                  updateConnections,
                                                  selectedColor
                                                )
                                              }
                                            />
                                          )}
                                        </SoundSelectorCointainer>
                                        <ConfigurationArrowsContainer>
                                          <Xarrow
                                            path="grid"
                                            startAnchor="right"
                                            endAnchor="left"
                                            start={`output_${dock.number}_${deviceIndex}`}
                                            end={`FrameLedColorSelector${device.id}`}
                                            key={`frameLedArrow${device.id}`}
                                            strokeWidth={2}
                                            headSize={4}
                                          />
                                        </ConfigurationArrowsContainer>
                                      </div>
                                    );
                                  })()}
                              </OutputDeviceProfileContainer>
                            </OutputDeviceProfileButtonContainer>
                          );
                        })}
                      </ScrollableWrapper>
                    </FlexContainer>
                  </FlexContainer>
                );
              })}
          </Row>
        ))}

        <Row>
          <FixedHeaderColumn>
            <SceneTitleWrapper>
              <SceneIcon fill="grey" />
              <Text color="font1">Location scenes</Text>
            </SceneTitleWrapper>
          </FixedHeaderColumn>
          <FlexContainer>
            <ScrollableWrapper />
            <ScrollableWrapper flexBasis="200" verticalAutoMargin={false}>
              {shouldPressProfileSelectionForScenesBeVisible(inputDevice) && (
                <PressProfilesContainer>
                  <PressProfileButton
                    active={pressProfile === PressProfiles.singlePress}
                    type="button"
                    id={`${PressProfiles.singlePress}${inputDevice.id}ProfileBtn`}
                    onClick={() => setPressProfile(PressProfiles.singlePress)}
                  >
                    Single press
                  </PressProfileButton>
                  <PressProfileButton
                    active={pressProfile === PressProfiles.doublePress}
                    type="button"
                    id={`${PressProfiles.doublePress}${inputDevice.id}ProfileBtn`}
                    onClick={() => setPressProfile(PressProfiles.doublePress)}
                  >
                    Double press
                  </PressProfileButton>
                </PressProfilesContainer>
              )}
            </ScrollableWrapper>
            <ScrollableWrapper>
              {scenes.map(scene => {
                const isActive = isSceneActive(
                  connectionsConfiguration,
                  inputDevice,
                  scene,
                  pressProfile
                );
                return (
                  <ConnectionDeviceButton
                    key={`device_${scene.id}`}
                    label={scene.name}
                    icon="devices-icons/relay"
                    active={isActive}
                    activeCircle={{
                      left: false
                    }}
                    disabled={
                      !previewMode &&
                      sceneDisabledBasedOnSceneConnection(
                        connectionsConfiguration,
                        pressProfile,
                        inputDevice,
                        scene
                      )
                    }
                    grayed={previewMode && !isActive}
                    id={
                      inputDevice && inputDevice.startsFrom === 'input'
                        ? composeSceneConnectionId(inputDevice, scene.id)
                        : `scene_${scene.id}`
                    }
                    onClick={() =>
                      onSceneItemClick(
                        scene,
                        inputDevice,
                        connectionsConfiguration,
                        updateConnections,
                        setInputDevice,
                        'output',
                        pressProfile,
                        previewMode
                      )
                    }
                    dataTip={`Scene: ${scene.name || 'unknown'}`}
                  />
                );
              })}
            </ScrollableWrapper>
          </FlexContainer>
        </Row>

        <ConfigurationArrowsContainer>
          {connectionsConfiguration
            .filter(({ connection }) => {
              return connectionArrowsFilterFunction(
                inputDevice,
                pressProfile,
                connection
              );
            })
            .map(({ connection }) => {
              const points = (): {
                start: string;
                end: string;
                key: string;
                startAnchor: string;
                endAnchor: string;
              } => {
                if (
                  inputDevice.connectionInputConfig &&
                  inputDevice.connectionInputConfig.targetAction ===
                    SCENE_ACTION
                ) {
                  return {
                    start: `${pressProfile}${inputDevice.connectionInputConfig.targetActionParameters.SceneId}ProfileBtn`,
                    end: `input_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}`,
                    key: `${connection.sourceDockNumber}${connection.sourceDeviceNumber}_${connection.targetDockNumber}${connection.targetActionParameters.SceneId}`,
                    startAnchor: 'left',
                    endAnchor: 'right'
                  };
                }
                if (
                  inputDevice &&
                  inputDevice.startsFrom === 'input' &&
                  connection.sourceDeviceNumber ===
                    connection.targetDeviceNumber &&
                  inputDevice.connectionInputConfig.sourceDeviceNumber ===
                    connection.sourceDeviceNumber
                ) {
                  return {
                    start: `${pressProfile}${inputDevice.id}ProfileBtn`,
                    end: composeSceneConnectionId(
                      inputDevice,
                      connection.targetActionParameters.SceneId
                    ),
                    key: `${connection.sourceDockNumber}${connection.sourceDeviceNumber}_${connection.targetDockNumber}${connection.targetActionParameters.SceneId}`,
                    startAnchor: 'right',
                    endAnchor: 'left'
                  };
                }
                if (inputDevice && inputDevice.startsFrom === 'input') {
                  return {
                    start: `${pressProfile}${inputDevice.id}ProfileBtn`,
                    end: `output_${connection.targetDockNumber}_${connection.targetDeviceNumber}`,
                    key: `${connection.sourceDockNumber}${connection.sourceDeviceNumber}_${connection.targetDockNumber}${connection.targetDeviceNumber}`,
                    startAnchor: 'right',
                    endAnchor: 'left'
                  };
                }
                return {
                  start: `${pressProfile}${inputDevice.id}ProfileBtn`,
                  end: `input_${connection.sourceDockNumber}_${connection.sourceDeviceNumber}`,
                  key: `${connection.targetDockNumber}${connection.targetDeviceNumber}_${connection.sourceDockNumber}${connection.sourceDeviceNumber}`,
                  startAnchor: 'left',
                  endAnchor: 'right'
                };
              };
              return (
                <Xarrow
                  path="grid"
                  startAnchor={points().startAnchor as anchorType}
                  endAnchor={points().endAnchor as anchorType}
                  start={points().start}
                  end={points().end}
                  key={points().key}
                  strokeWidth={2}
                  headSize={4}
                  // @ts-expect-error
                  title={`input_${inputDevice.connectionInputConfig.sourceDockNumber}_${inputDevice.connectionInputConfig.sourceDeviceNumber}=${refresh}`}
                />
              );
            })}
          {/* Arrow from the input device to PressProfile */}
          {inputDevice && inputDevice.startsFrom === 'input' && (
            <AnchorFromInput
              inputDevice={inputDevice}
              pressProfile={pressProfile}
              refresh={refresh}
            />
          )}
          {/* Arrow from the output device to PressProfile */}
          <AnchorFromOutput
            inputDevice={inputDevice}
            pressProfile={pressProfile}
            refresh={refresh}
          />
        </ConfigurationArrowsContainer>
      </ScrollableContainer>
    </MainContainer>
  );
};

export default AdvancedConnectionsPanel;
